<template>
    <div class="ls-flow--container">
        <div test-id="form_wrapper">
            <div class="ls-flow--title">Confirm Dispute</div>

            <form v-if="step === 'initial'" novalidate @submit.prevent="createDisputeCompletion">
                <div test-id="evidence_state">
                    <LsInputLabel label="Job evidence at filing" />
                    <div>After photo: {{ hasAfterPhoto ? 'Uploaded' : 'Missing' }}</div>
                    <div>Geofence entry: {{ geofenceEntered ? 'Recorded' : 'Missing' }}</div>
                    <div test-id="obstruction_report">
                        Obstruction report: {{ obstructionReportLabel }}
                    </div>
                    <div test-id="dispute_response_deadline">
                        Response deadline: {{ disputeResponseDeadlineLabel }}
                    </div>
                    <div test-id="payout_hold">Payout hold: {{ payoutHoldLabel }}</div>
                </div>

                <div v-if="scheduleDetails.last_completed_schedule_event.images.length">
                    <LsInputLabel label="Job photos uploaded by the provider" />
                    <LsImageRow :images="scheduleDetails.last_completed_schedule_event.images" />
                </div>

                <LsRadios
                    v-model="dispute_type"
                    track-id="dispute_view-dispute_type"
                    name="dispute_type"
                    label="Reason for return request"
                    :options="disputeTypeOptions"
                    :validation="{ required: true }"
                    @input="resetReason"
                />

                <LsSelect
                    v-if="showReasonSelect"
                    v-model="reason"
                    track-id="dispute_view-reason"
                    name="reason"
                    label="What was the quality problem?"
                    :options="reasonOptions"
                    :validation="{ required: true }"
                    class="ls-flow--330"
                    @input="resetNotes"
                />

                <LsInput
                    v-else-if="showReasonInput"
                    v-model="reason"
                    track-id="dispute_view-reason"
                    name="reason"
                    label="What was the quality problem?"
                    :validation="{ required: true }"
                    type="textarea"
                    placeholder="Enter details"
                />

                <LsCheckboxes
                    v-model="locations"
                    track-id="dispute_view-locations"
                    name="locations"
                    label="Where was the issue on the property?"
                    :options="locationsOptions"
                    :validation="{ required: true }"
                    @input="resetNotes"
                />

                <LsInput
                    v-if="showNotes"
                    v-model="notes"
                    track-id="dispute_view-notes"
                    label="What are the additional details for this issue?"
                    name="notes"
                    type="textarea"
                    placeholder="Enter details"
                    :validation="{ required: true }"
                />

                <LsInput
                    v-model="zendesk_ticket_id"
                    track-id="dispute_view-zendesk_ticket_id"
                    label="Zendesk ticket number"
                    name="zendesk_ticket_id"
                    type="number"
                    placeholder="Enter number"
                    :validation="{ required: true, min_value: 1000000, max: 8 }"
                />

                <LsError v-if="isInvalid" />

                <LsButton
                    track-id="dispute_view-continue"
                    text="Continue"
                    :loading="isUpdating1"
                    type="submit"
                />
            </form>

            <form v-if="step === 'fullCredit'" novalidate>
                <LsInfoCard>
                    This customer qualifies for Full Credit and a Pro Change. Confirming will issue
                    a full credit and automatically assign a new provider.
                </LsInfoCard>

                <div class="ls-flow--button-row">
                    <LsButton
                        track-id="dispute_view-full_credit"
                        text="Full Credit + Change Pro"
                        :loading="isUpdating3"
                        @click="disputeCompletionFullCredit"
                    />
                </div>
            </form>

            <form v-if="step === 'fixOrChangePro'" novalidate>
                <LsInfoCard>
                    If you choose Fix Issue, we will ask the current provider to return to the
                    customer's property to fix the issue. If you choose Change Pro, a new provider
                    will be assigned to the customer.
                </LsInfoCard>

                <div class="ls-flow--button-row">
                    <LsButton
                        track-id="dispute_view-fix_issue"
                        text="Fix Issue"
                        :loading="isUpdating1"
                        :disabled="isUpdating2"
                        @click="disputeCompletionChooseFix"
                    />
                    <LsButton
                        track-id="dispute_view-change_pro"
                        text="Change Pro"
                        :loading="isUpdating2"
                        :disabled="isUpdating1"
                        @click="disputeCompletionChangePro"
                    />
                </div>
            </form>

            <form v-if="step === 'fixOrFeedback'" novalidate>
                <LsInfoCard>
                    If you choose Fix Issue, we will ask the current provider to return to the
                    customer's property to fix the issue. If you choose Send Feedback, the
                    customer's feedback will be shared with the provider and the dispute will be
                    resolved without a credit or a pro change.
                </LsInfoCard>

                <div class="ls-flow--button-row">
                    <LsButton
                        track-id="dispute_view-fix_issue"
                        text="Fix Issue"
                        :loading="isUpdating1"
                        :disabled="isUpdating2"
                        @click="disputeCompletionChooseFix"
                    />
                    <LsButton
                        track-id="dispute_view-feedback"
                        text="Send Feedback"
                        :loading="isUpdating2"
                        :disabled="isUpdating1"
                        @click="disputeCompletionFeedback"
                    />
                </div>
            </form>

            <form v-if="step === 'fixOrCredit' || step === 'fixOrDecline'" novalidate>
                <LsInfoCard>
                    If you choose Fix Issue, we will ask the current provider to return to the
                    customer's property to fix the issue.
                </LsInfoCard>

                <div class="ls-flow--button-row">
                    <LsButton
                        track-id="dispute_view-fix_issue"
                        text="Fix Issue"
                        :loading="isUpdating1"
                        :disabled="isUpdating2"
                        @click="disputeCompletionChooseFix"
                    />
                    <LsButton
                        v-if="step === 'fixOrCredit'"
                        track-id="dispute_view-credit"
                        :text="`$${disputeCompletion.credit_offered_amount} Credit`"
                        :loading="isUpdating2"
                        :disabled="isUpdating1"
                        @click="disputeCompletionChooseCredit"
                    />
                    <LsButton
                        v-else
                        track-id="dispute_view-decline"
                        text="Issue Refund Without Fix"
                        :loading="isUpdating2"
                        :disabled="isUpdating1"
                        @click="disputeCompletionDeclineFix"
                    />
                </div>
            </form>

            <form v-if="step === 'fixFirst'" novalidate>
                <LsInfoCard>
                    Offer the customer the fix first. If you choose Fix Issue, we will ask the
                    current provider to return to the customer's property to fix the issue.
                </LsInfoCard>

                <div class="ls-flow--button-row">
                    <LsButton
                        track-id="dispute_view-fix_issue"
                        text="Fix Issue"
                        :loading="isUpdating1"
                        :disabled="isUpdating2"
                        @click="disputeCompletionChooseFix"
                    />
                    <LsButton
                        track-id="dispute_view-fix_declined"
                        text="Customer declined the fix"
                        :disabled="isUpdating1"
                        @click="step = 'fixThenCredit'"
                    />
                </div>
            </form>

            <form v-if="step === 'fixThenCredit'" novalidate>
                <LsInfoCard>
                    The customer declined the fix. You can offer the credit below, or go back and
                    ask the provider to return and fix the issue.
                </LsInfoCard>

                <div class="ls-flow--button-row">
                    <LsButton
                        track-id="dispute_view-credit"
                        :text="`$${disputeCompletion.credit_offered_amount} Credit`"
                        :loading="isUpdating2"
                        :disabled="isUpdating1"
                        @click="disputeCompletionChooseCredit"
                    />
                    <LsButton
                        track-id="dispute_view-back_to_fix"
                        text="Ask provider to return and fix"
                        :loading="isUpdating1"
                        :disabled="isUpdating2"
                        @click="disputeCompletionChooseFix"
                    />
                </div>
            </form>

            <form v-if="step === 'sameOrDifferentPro'" novalidate>
                <LsInfoCard>
                    If you choose Different Provider, the full payout will be pulled from current
                    provider and the provider claiming this job will be offered the full payout.
                </LsInfoCard>

                <div class="ls-flow--button-row">
                    <LsButton
                        track-id="dispute_view-same_provider"
                        text="Same Provider"
                        :disabled="isUpdating2"
                        @click="step = 'fixWithReturnTripfeeOverride'"
                    />
                    <LsButton
                        track-id="dispute_view-different_provider"
                        text="Different Provider"
                        :loading="isUpdating2"
                        @click="disputeCompletionChooseNewPro"
                    />
                </div>
            </form>

            <form
                v-if="step === 'fixWithReturnTripfeeOverride'"
                novalidate
                @submit.prevent="disputeCompletionChooseFix"
            >
                <LsInfoCard>
                    Select the amount to be paid out to current provider for returning to fix.
                    Please note that the provider will get full payout for the job in addition to
                    the amount you select below.
                </LsInfoCard>

                <LsSelect
                    v-model="return_tripfee_override"
                    track-id="dispute_view-return_tripfee_override"
                    name="return_tripfee_override"
                    label="Payout for the current provider"
                    :options="returnTripfeeOverrideOptions"
                    :validation="{ required: true }"
                    class="ls-flow--330"
                />

                <LsError v-if="isInvalid" />

                <LsButton
                    track-id="dispute_view-confirm_dispute"
                    text="Confirm Dispute"
                    :loading="isUpdating1"
                    type="submit"
                />
            </form>
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import { lsErrorService, lsModalService, lsScheduleHelpers } from 'ls-vue-common';
import gql from 'graphql-tag';
import LsImageRow from '@/components/LsImageRow/LsImageRow.vue';
import {
    LsButton,
    LsInput,
    LsCheckboxes,
    LsRadios,
    LsSelect,
    LsInfoCard,
    LsInputLabel,
} from '@/components/common';
import LsError from '@/components/common/LsError/LsError.vue';
import CreateDisputeCompletion from '@/graphql/mutations/CreateDisputeCompletion.gql';
import DisputeCompletionChooseCredit from '@/graphql/mutations/DisputeCompletionChooseCredit.gql';
import DisputeCompletionChooseFix from '@/graphql/mutations/DisputeCompletionChooseFix.gql';
import DisputeCompletionDeclineFix from '@/graphql/mutations/DisputeCompletionDeclineFix.gql';
import DisputeCompletionChooseNewPro from '@/graphql/mutations/DisputeCompletionChooseNewPro.gql';
import DisputeCompletionFeedback from '@/graphql/mutations/DisputeCompletionFeedback.gql';
import ChangeMyPro from '@/graphql/mutations/ChangeMyPro.gql';
import DisputeCompletionFullCredit from '@/graphql/mutations/DisputeCompletionFullCredit.gql';
import Schedule from '@/graphql/queries/Schedule.gql';
import { FlowSuccessModal } from '@/modals';
import { apolloClient } from '@/vue-apollo';
import { lsHelpers } from '@/helpers';
import { hasCustomerFeature } from '@/helpers/flagsmith';

const disputeTypeOptions = [
    {
        value: 'quality',
        label: 'Bad job',
    },
    {
        value: 'partial_complete',
        label: 'Partial complete',
    },
    {
        value: 'no_work_done',
        label: 'Nobody showed',
    },
];

const reasonOptions = [
    {
        value: 'grass.uneven',
        label: 'Uneven mow',
    },
    {
        value: 'missedspots',
        label: 'Missed spots',
    },
    {
        value: 'edging',
        label: 'Bad edging',
    },
    {
        value: 'blowing',
        label: 'Issue blowing',
    },
    {
        value: 'grass.long',
        label: 'Grass too long',
    },
    {
        value: 'other',
        label: 'Other',
    },
];

const locationsOptions = [
    {
        value: 'full_yard',
        label: 'Full yard',
    },
    {
        value: 'front_yard',
        label: 'Front-yard',
    },
    {
        value: 'back_yard',
        label: 'Backyard',
    },
    {
        value: 'left_yard',
        label: 'Left side',
    },
    {
        value: 'right_yard',
        label: 'Right side',
    },
    {
        value: 'other_yard',
        label: 'Other',
    },
];

const returnTripfeeOverrideOptions = [
    { value: 0, label: '$0.00' },
    { value: 5, label: '$5.00' },
    { value: 10, label: '$10.00' },
    { value: 15, label: '$15.00' },
];

const payoutHoldReasonLabels = {
    expired: 'Expired',
    rejected: 'Rejected',
    change_pro_no_credit: 'Change Pro, no credit',
    no_evidence: 'No evidence',
    no_show: 'No-show',
    property_drop: 'Property drop',
    'dispute.completion.auto_redistributed.fix': 'Auto distribution fix',
    'dispute.completion.auto_redistributed.credit': 'Auto distribution credit',
    photo_set_failure: 'Photos do not show work',
    high_mowing_need: 'Lawn not mowed',
};

const M8_FIX_FIRST_FLAG = 'disputes:m8_fix_first_screen';
const IN_SCOPE_FIX_FIRST_DISPUTE_TYPES = ['quality', 'partial_complete'];
const DISPUTE_CHANGE_PRO_REASON = 'customer.dispute_completion.m6';
const CHANGE_PRO_SUCCESS_COPY =
    'We are finding a new Pro for this customer. They will be assigned a new Pro moving forward.';

export default {
    name: 'DisputeView',
    components: {
        LsButton,
        LsInput,
        LsError,
        LsImageRow,
        LsCheckboxes,
        LsRadios,
        LsSelect,
        LsInfoCard,
        LsInputLabel,
    },
    $_veeValidate: {
        validator: 'new',
    },
    data() {
        const scheduleEventId = _.toNumber(_.get(this.$route.query, 'schedule_event_id'));

        return {
            loading: 0,
            isUpdating1: false,
            isUpdating2: false,
            isUpdating3: false,
            isInvalid: false,

            disputeTypeOptions,
            reasonOptions,
            locationsOptions,
            returnTripfeeOverrideOptions,

            // form data
            dispute_type: null,
            reason: null,
            locations: [],
            notes: '',
            zendesk_ticket_id: '',
            return_tripfee_override: null,

            scheduleDetails: {},
            disputeCompletion: {},
            m8FixFirstEnabled: false,
            step: 'initial', // fullCredit - fixOrChangePro - fixOrCredit - fixOrDecline - sameOrDifferentPro - fixWithReturnTripfeeOverride
            property: apolloClient.readFragment({
                id: `Property:${this.$route.params.property_id}`,
                fragment: gql`
                    fragment property on Property {
                        street
                    }
                `,
            }),
            variables: {
                customer_id: _.toNumber(this.$route.params.customer_id),
                property_id: _.toNumber(this.$route.params.property_id),
                schedule_id: _.toNumber(this.$route.params.schedule_id),
                ...(_.isFinite(scheduleEventId) && scheduleEventId > 0
                    ? { schedule_event_id: scheduleEventId }
                    : {}),
            },
        };
    },
    computed: {
        hasAfterPhoto() {
            return _.get(
                this.scheduleDetails,
                'last_completed_schedule_event.has_after_photo',
                false
            );
        },
        geofenceEntered() {
            return _.get(
                this.scheduleDetails,
                'last_completed_schedule_event.geofence_entered',
                false
            );
        },
        obstructionReportLabel() {
            const hasReport = !!_.get(
                this.scheduleDetails,
                'last_completed_schedule_event.has_partial_completion_report',
                false
            );

            if (!hasReport) {
                return 'None';
            }

            const reason = _.get(
                this.scheduleDetails,
                'last_completed_schedule_event.partial_completion_reason',
                ''
            );

            return reason ? `Filed (${reason})` : 'Filed';
        },
        disputeResponseDeadlineLabel() {
            const deadline = _.get(
                this.scheduleDetails,
                'last_completed_schedule_event.dispute_context.dispute_response_deadline',
                null
            );

            if (!deadline) {
                return 'N/A';
            }

            const stage = _.get(
                this.scheduleDetails,
                'last_completed_schedule_event.dispute_context.dispute_warning_stage',
                null
            );

            const deadlineLabel = lsHelpers.parseTimestamp({ timestamp: deadline });

            // A stage of 0 is an open window with no reminder sent yet, which the API
            // contract keeps distinct from a closed window's null - so gate on nil, not
            // on falsiness, or "0 warnings" silently reads as "no warning data".
            return _.isNil(stage)
                ? deadlineLabel
                : `${deadlineLabel} - ${stage} of 2 warnings sent`;
        },
        payoutHoldLabel() {
            const holdReason = _.get(
                this.scheduleDetails,
                'last_completed_schedule_event.dispute_context.hold_reason',
                null
            );
            if (!holdReason) {
                return 'None';
            }
            return payoutHoldReasonLabels[holdReason] || holdReason;
        },
        isMowing() {
            return lsHelpers.isMowing({ schedule: this.scheduleDetails.schedule });
        },
        isInstantQuote() {
            return this.scheduleDetails.schedule.type === 'instant_quote';
        },
        showReasonSelect() {
            return this.isMowing && this.dispute_type === 'quality';
        },
        showReasonInput() {
            return (
                !this.isMowing &&
                (this.dispute_type === 'quality' || this.dispute_type === 'partial_complete')
            );
        },
        showNotes() {
            return _.includes(this.locations, 'other_yard') || this.reason === 'other';
        },
        isFixFirstEligibleDisputeType() {
            return _.includes(IN_SCOPE_FIX_FIRST_DISPUTE_TYPES, this.dispute_type);
        },
    },
    created() {
        this.evaluateFixFirstFlag();
    },
    apollo: {
        scheduleDetails: {
            query: Schedule,
            variables() {
                return this.variables;
            },
            update(data) {
                if (!lsHelpers.isMowing({ schedule: data.schedule.schedule })) {
                    this.reason = '';
                }
                return data.schedule;
            },
        },
    },
    methods: {
        async evaluateFixFirstFlag() {
            const customer = apolloClient.readFragment({
                id: `Customer:${this.variables.customer_id}`,
                fragment: gql`
                    fragment customerUserHashId on Customer {
                        user {
                            hash_id
                        }
                    }
                `,
            });
            this.m8FixFirstEnabled = await hasCustomerFeature(
                _.get(customer, 'user.hash_id'),
                M8_FIX_FIRST_FLAG
            );
        },
        resetReason() {
            if (this.isMowing && !this.showReasonSelect) {
                this.reason = null;
            } else if (!this.isMowing && !this.showReasonInput) {
                this.reason = '';
            }
        },
        resetNotes() {
            if (!this.showNotes) {
                this.notes = '';
            }
        },
        openModal({ copy }) {
            lsModalService.open({
                parent: this,
                component: FlowSuccessModal,
                props: {
                    title: `${lsScheduleHelpers.getScheduleServiceName({
                        schedule: this.scheduleDetails.schedule,
                        nameOnly: true,
                    })} dispute for ${this.property.street}`,
                    copy,
                    replaceRoute: true,
                },
            });
        },
        async createDisputeCompletion() {
            const valid = await this.$validator.validateAll();
            this.isInvalid = !valid;
            if (!valid) {
                return;
            }

            this.isUpdating1 = true;
            this.$apollo
                .mutate({
                    mutation: CreateDisputeCompletion,
                    variables: {
                        ...this.variables,
                        dispute_type: this.dispute_type,
                        reason: this.reason,
                        ..._.reduce(
                            this.locations,
                            (result, location) => {
                                result[location] = true;
                                return result;
                            },
                            {}
                        ),
                        notes: this.notes,
                        zendesk_ticket_id: this.zendesk_ticket_id
                            ? _.toInteger(this.zendesk_ticket_id)
                            : null,
                    },
                })
                .then(({ data }) => {
                    this.disputeCompletion = data.createDisputeCompletion;
                    if (this.disputeCompletion.qualifies_for_ms1) {
                        this.step = 'fullCredit';
                    } else if (
                        this.disputeCompletion.options_pending_choice &&
                        this.disputeCompletion.options_choice_variant === 'fix_or_change_pro'
                    ) {
                        this.step = 'fixOrChangePro';
                    } else if (
                        this.disputeCompletion.options_pending_choice &&
                        this.disputeCompletion.options_choice_variant === 'fix_or_feedback'
                    ) {
                        this.step = 'fixOrFeedback';
                    } else if (
                        this.disputeCompletion.credit_offered_at &&
                        this.disputeCompletion.credit_offered_amount
                    ) {
                        this.step =
                            this.m8FixFirstEnabled && this.isFixFirstEligibleDisputeType
                                ? 'fixFirst'
                                : 'fixOrCredit';
                    } else {
                        this.step = 'fixOrDecline';
                    }

                    apolloClient.writeQuery({
                        query: Schedule,
                        variables: this.variables,
                        data: {
                            schedule: {
                                ...this.scheduleDetails,
                                last_completed_schedule_event: {
                                    ...this.scheduleDetails.last_completed_schedule_event,
                                    is_completion_disputable: false,
                                },
                                latest_dispute_completions_count:
                                    this.scheduleDetails.latest_dispute_completions_count + 1,
                            },
                        },
                    });
                })
                .catch((error) => {
                    lsErrorService.log({ error, showAlert: true });
                })
                .finally(() => {
                    this.isUpdating1 = false;
                });
        },
        async disputeCompletionChooseFix() {
            if (this.step === 'fixWithReturnTripfeeOverride') {
                const valid = await this.$validator.validateAll();
                this.isInvalid = !valid;
                if (!valid) {
                    return;
                }
            } else if (this.isInstantQuote) {
                this.step = 'sameOrDifferentPro';
                return;
            }

            this.isUpdating1 = true;
            this.$apollo
                .mutate({
                    mutation: DisputeCompletionChooseFix,
                    variables: {
                        ...this.variables,
                        id: _.toNumber(this.disputeCompletion.id),
                        return_tripfee_override: this.return_tripfee_override,
                    },
                })
                .then(({ data }) => {
                    this.openModal({ copy: data.disputeCompletionChooseFix.copy });
                })
                .catch((error) => {
                    lsErrorService.log({ error, showAlert: true });
                })
                .finally(() => {
                    this.isUpdating1 = false;
                });
        },
        disputeCompletionChooseCredit() {
            this.isUpdating2 = true;
            this.$apollo
                .mutate({
                    mutation: DisputeCompletionChooseCredit,
                    variables: {
                        ...this.variables,
                        id: _.toNumber(this.disputeCompletion.id),
                    },
                })
                .then(({ data }) => {
                    this.openModal({ copy: data.disputeCompletionChooseCredit.copy });
                })
                .catch((error) => {
                    lsErrorService.log({ error, showAlert: true });
                })
                .finally(() => {
                    this.isUpdating2 = false;
                });
        },
        disputeCompletionDeclineFix() {
            this.isUpdating2 = true;
            this.$apollo
                .mutate({
                    mutation: DisputeCompletionDeclineFix,
                    variables: {
                        ...this.variables,
                        id: _.toNumber(this.disputeCompletion.id),
                    },
                })
                .then(({ data }) => {
                    this.openModal({ copy: data.disputeCompletionDeclineFix.copy });
                })
                .catch((error) => {
                    lsErrorService.log({ error, showAlert: true });
                })
                .finally(() => {
                    this.isUpdating2 = false;
                });
        },
        disputeCompletionChooseNewPro() {
            this.isUpdating2 = true;
            this.$apollo
                .mutate({
                    mutation: DisputeCompletionChooseNewPro,
                    variables: {
                        ...this.variables,
                        id: _.toNumber(this.disputeCompletion.id),
                    },
                })
                .then(({ data }) => {
                    this.openModal({ copy: data.disputeCompletionChooseNewPro.copy });
                })
                .catch((error) => {
                    lsErrorService.log({ error, showAlert: true });
                })
                .finally(() => {
                    this.isUpdating2 = false;
                });
        },
        disputeCompletionChangePro() {
            this.isUpdating2 = true;
            const contractorId = _.get(this.scheduleDetails, 'schedule.contractor.id');
            this.$apollo
                .mutate({
                    mutation: ChangeMyPro,
                    variables: {
                        customer_id: this.variables.customer_id,
                        property_id: this.variables.property_id,
                        contractor_id: _.toNumber(contractorId),
                        reason: DISPUTE_CHANGE_PRO_REASON,
                    },
                })
                .then(() => {
                    this.openModal({ copy: CHANGE_PRO_SUCCESS_COPY });
                })
                .catch((error) => {
                    lsErrorService.log({ error, showAlert: true });
                })
                .finally(() => {
                    this.isUpdating2 = false;
                });
        },
        disputeCompletionFeedback() {
            this.isUpdating2 = true;
            this.$apollo
                .mutate({
                    mutation: DisputeCompletionFeedback,
                    variables: {
                        ...this.variables,
                        id: _.toNumber(this.disputeCompletion.id),
                    },
                })
                .then(({ data }) => {
                    this.openModal({ copy: data.disputeCompletionFeedback.copy });
                })
                .catch((error) => {
                    lsErrorService.log({ error, showAlert: true });
                })
                .finally(() => {
                    this.isUpdating2 = false;
                });
        },
        disputeCompletionFullCredit() {
            this.isUpdating3 = true;
            this.$apollo
                .mutate({
                    mutation: DisputeCompletionFullCredit,
                    variables: {
                        ...this.variables,
                        id: _.toNumber(this.disputeCompletion.id),
                    },
                })
                .then(({ data }) => {
                    this.openModal({ copy: data.disputeCompletionFullCredit.copy });
                })
                .catch((error) => {
                    lsErrorService.log({ error, showAlert: true });
                })
                .finally(() => {
                    this.isUpdating3 = false;
                });
        },
    },
};
</script>
